import { useEffect, useRef } from 'react'

/**
 * Scrubs a <video> back and forth based on horizontal mouse movement.
 * Moving the cursor right advances the video; moving left rewinds it.
 *
 * Returns a ref to attach to the target <video> element. The video should NOT
 * autoplay — this hook drives `currentTime` manually.
 *
 * Performance: video seeking is async and flooding `currentTime` causes the
 * browser to drop seeks. We gate seeks behind a `seeking` flag and use the
 * `seeked` event to catch up to the latest target time.
 */
export function useMouseScrubVideo<
  T extends HTMLVideoElement = HTMLVideoElement,
>() {
  const videoRef = useRef<T | null>(null)

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    // Sensitivity: fraction of the full duration covered by dragging across
    // the entire viewport width.
    const SENSITIVITY = 0.8

    let prevX: number | null = null
    let targetTime = 0
    let seeking = false

    const clamp = (value: number, min: number, max: number) =>
      Math.min(Math.max(value, min), max)

    const onLoadedMetadata = () => {
      // Seek to a hair past 0 so the first frame paints immediately on load.
      targetTime = 0.001
      try {
        video.currentTime = 0.001
      } catch {
        /* duration not ready yet — ignore */
      }
    }

    const onSeeked = () => {
      if (Math.abs(video.currentTime - targetTime) > 0.01) {
        // We fell behind the latest target — keep chasing it.
        video.currentTime = targetTime
      } else {
        seeking = false
      }
    }

    const onMouseMove = (e: MouseEvent) => {
      if (!video.duration || Number.isNaN(video.duration)) return

      if (prevX === null) {
        prevX = e.clientX
        return
      }

      const delta = e.clientX - prevX
      prevX = e.clientX

      const offset = (delta / window.innerWidth) * SENSITIVITY * video.duration
      targetTime = clamp(targetTime + offset, 0, video.duration)

      if (!seeking) {
        seeking = true
        video.currentTime = targetTime
      }
    }

    video.addEventListener('loadedmetadata', onLoadedMetadata)
    video.addEventListener('seeked', onSeeked)
    window.addEventListener('mousemove', onMouseMove)

    // If metadata is already available (e.g. cached), kick the first frame.
    if (video.readyState >= 1) {
      onLoadedMetadata()
    }

    return () => {
      video.removeEventListener('loadedmetadata', onLoadedMetadata)
      video.removeEventListener('seeked', onSeeked)
      window.removeEventListener('mousemove', onMouseMove)
    }
  }, [])

  return videoRef
}
